import { Injectable, inject, signal, OnDestroy } from '@angular/core';
import { Firestore, doc, getDoc, onSnapshot, collection } from '@angular/fire/firestore';
import { AuthService } from '@core/auth.service';
import {
  ChallengeAnimationState,
  ChallengeLookupNew,
  GameChallenges,
  PendingChallengeAnimation,
  PowerBarData,
  WeeklyChallenges,
} from '../models/game.model';
import { EMPTY, Observable } from 'rxjs';
import { Data } from '@angular/router';
import { GameAnalyticsService } from '../core/game-analytics.service';
@Injectable({
  providedIn: 'root',
})
export class GameDataService implements OnDestroy {
  private firestore = inject(Firestore);
  private authService = inject(AuthService);
  private gameAnalyticsService = inject(GameAnalyticsService);

  private userId = '';
  private gameDataUnsubscribe?: () => void;
  private challengesUnsubscribe?: () => void;

  // Signals
  powerBarData = signal<PowerBarData | null>(null);
  challengeHudData = signal<GameChallenges | null>(null);
  weeklyChallengesData = signal<WeeklyChallenges | null>(null);
  challengesBannerData = signal<unknown[] | null>(null);
  pendingChallengeAnimations = signal<PendingChallengeAnimation[]>([]);
  /**
   * True while `users/.../challenges_new/onboarding` exists and
   * `settings.completed` is not true — blocks time-based challenge animations.
   */
  isChallengesOnboardingIncomplete = signal(false);
  /** Global lookup for challenge / weekly prize pop-up copy and assets. */
  challengeLookupData = signal<ChallengeLookupNew | null>(null);
  constructor() {
    this.init();
  }

  private async init() {
    try {
      await this.loadChallengeLookup();
      this.userId = await this.authService.getUid('GameDataService');
      if (this.userId) {
        this.subscribeToGameData(this.userId);
        this.subscribeToWeeklyChallenges(this.userId);
      }
    } catch (error) {
      this.authService.errorHandlerLogs('[GameDataService] Error initializing:', error);
    }
  }

  /** One-shot fetch of `/Global_lookup/challenge_lookup_new` for pop-up localisation + assets. */
  private async loadChallengeLookup(): Promise<void> {
    try {
      const docRef = doc(this.firestore, `/Global_lookup/challenge_lookup_new`);
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        this.challengeLookupData.set(snapshot.data() as ChallengeLookupNew);
      } else {
        this.challengeLookupData.set(null);
      }
    } catch (error) {
      this.authService.errorHandlerLogs('[GameDataService] Error loading challenge_lookup_new:', error);
      this.challengeLookupData.set(null);
    }
  }

  /** Subscribe to real-time updates for main game data (hud + power bar) */
  private subscribeToGameData(userId?: string) {
    const uid = userId ?? this.userId;
    if (!uid) return;

    // Clean up existing subscription if any
    if (this.gameDataUnsubscribe) {
      this.gameDataUnsubscribe();
    }

    try {
      const docRef = doc(this.firestore, `users/${uid}/main_pages/game_new`);
      
      this.gameDataUnsubscribe = onSnapshot(
        docRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            this.challengeHudData.set(data?.challenge_hud ?? null);
            this.powerBarData.set(data?.power_bar ?? null);
          } else {
            this.challengeHudData.set(null);
            this.powerBarData.set(null);
          }
        },
        (error) => {
          this.authService.errorHandlerLogs('[GameDataService] Error subscribing to game data:', error);
          this.challengeHudData.set(null);
          this.powerBarData.set(null);
        }
      );
    } catch (error) {
      this.authService.errorHandlerLogs('[GameDataService] Error setting up game data subscription:', error);
    }
  }

  /** Subscribe to real-time updates for weekly challenges data */
  private subscribeToWeeklyChallenges(userId?: string) {
    const uid = userId ?? this.userId;
    if (!uid) return;

    // Clean up existing subscription if any
    if (this.challengesUnsubscribe) {
      this.challengesUnsubscribe();
    }

    try {
      const colRef = collection(this.firestore, `users/${uid}/challenges_new`);
      
      this.challengesUnsubscribe = onSnapshot(
        colRef,
        (snapshot) => {
          const docs = snapshot.docs.map(d => ({ id: d.id, ...(d.data() as Record<string, unknown>) })) as Array<{ id: string } & Record<string, unknown>>;

          let weeklyData: WeeklyChallenges | null = null;
          const bannerData: unknown[] = [];
          const pending: PendingChallengeAnimation[] = [];
          // Loop through each document
          type AnyDoc = {
            id: string;
            settings?: { challenge_type?: unknown; completed?: unknown };
            animation?: ChallengeAnimationState;
          } & Record<string, unknown>;

          const onboardingIncomplete = this.resolveOnboardingIncomplete(docs as AnyDoc[]);
          this.isChallengesOnboardingIncomplete.set(onboardingIncomplete);

          for (const raw of docs) {
            const doc = raw as AnyDoc;
            // Prefer explicit settings.challenge_type check (falls back to id check)
            const isWeeklyBySettings =
              typeof doc.settings?.challenge_type === 'string' &&
              (doc.settings.challenge_type as string).toLowerCase() === 'weekly';
            const isWeeklyById = typeof doc.id === 'string' && doc.id.toLowerCase().includes('weekly');
            const challengeType =
              typeof doc.settings?.challenge_type === 'string'
                ? (doc.settings.challenge_type as string)
                : doc.id;
            const isOnboardingDoc = this.isOnboardingChallengeDoc(doc.id, challengeType);

            if (isWeeklyBySettings || isWeeklyById) {
              // store the first weekly doc (if there are multiple you can change this to an array)
              if (!weeklyData) weeklyData = (doc as unknown) as WeeklyChallenges;
            } else {
              bannerData.push(doc);
            }

            if (doc.animation?.pending) {
              // While onboarding is incomplete, never queue competitive/luck/mastery/weekly flies.
              if (onboardingIncomplete && !isOnboardingDoc) {
                continue;
              }
              pending.push({
                id: doc.id,
                challenge_type: challengeType,
                animation: doc.animation,
              });
            }
          }

          pending.sort(
            (a, b) =>
              this.bufferTimestampMs(a.animation.buffered_timestamp) -
              this.bufferTimestampMs(b.animation.buffered_timestamp)
          );

          // Update signals with real-time data
          this.weeklyChallengesData.set(weeklyData);
          this.challengesBannerData.set(bannerData.length ? bannerData : null);
          this.pendingChallengeAnimations.set(pending);

          const allChallenges = [...bannerData, ...(weeklyData ? [weeklyData] : [])] as Array<
            Record<string, unknown> & { id?: string }
          >;
          this.gameAnalyticsService.observeChallengeSnapshots(allChallenges);
        },
        (error) => {
          this.authService.errorHandlerLogs('[GameDataService] Error subscribing to challenges:', error);
          this.weeklyChallengesData.set(null);
          this.challengesBannerData.set(null);
          this.pendingChallengeAnimations.set([]);
          this.isChallengesOnboardingIncomplete.set(false);
        }
      );
    } catch (error) {
      this.authService.errorHandlerLogs('[GameDataService] Error setting up challenges subscription:', error);
    }
  }

  /**
   * `challenges_new/onboarding` with `settings.completed !== true` → onboarding still in progress.
   * No onboarding doc → treat as completed (normal time-challenge flow).
   */
  private resolveOnboardingIncomplete(
    docs: Array<{ id: string; settings?: { challenge_type?: unknown; completed?: unknown } }>
  ): boolean {
    const onboardingDoc = docs.find((doc) => {
      const type =
        typeof doc.settings?.challenge_type === 'string'
          ? doc.settings.challenge_type
          : doc.id;
      return this.isOnboardingChallengeDoc(doc.id, type);
    });
    if (!onboardingDoc) {
      return false;
    }
    const completed = onboardingDoc.settings?.completed;
    return completed !== true && completed !== 'true';
  }

  private isOnboardingChallengeDoc(id: string, challengeType: string): boolean {
    const type = String(challengeType || '').toLowerCase();
    const docId = String(id || '').toLowerCase();
    return type === 'onboarding' || type.includes('onboarding') || docId === 'onboarding';
  }

  /** Normalize animation.buffered_timestamp (seconds, ms, or Firestore Timestamp) to ms. */
  private bufferTimestampMs(
    bufferedTimestamp: ChallengeAnimationState['buffered_timestamp']
  ): number {
    if (typeof bufferedTimestamp === 'number') {
      return bufferedTimestamp < 1e12 ? bufferedTimestamp * 1000 : bufferedTimestamp;
    }
    if (bufferedTimestamp && typeof bufferedTimestamp === 'object') {
      return (bufferedTimestamp.seconds ?? 0) * 1000;
    }
    return 0;
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    if (this.gameDataUnsubscribe) {
      this.gameDataUnsubscribe();
    }
    if (this.challengesUnsubscribe) {
      this.challengesUnsubscribe();
    }
  }
  async updateExpiredBannerFlag() {
  const uid = this.userId;
  if (!uid) return;

  const banners = this.challengesBannerData();
  if (!banners || !Array.isArray(banners)) return;

  const currentSeconds = Math.floor(Date.now() / 1000);

  const promises = banners.map((banner: any) => {
    const expirySeconds = banner?.target?.expiry_date?.seconds;
    const isExpired =
      expirySeconds && currentSeconds >= expirySeconds;

    if (!isExpired || !banner?.id) return Promise.resolve();

    const path_id = `O9EUAKEWDHwbzldxFQUJ`;

    const dv = {
      docId: banner.id,
      data: {
        settings: {
          expired: true
        }
      }
    };

    return this.authService.getV2CloudFunction('updatefirestoredoc', {
      path_id,
      dv,
      method: 'update'
    });
  });

    await Promise.all(promises);
  }

  /**
   * Claim challenges reward by calling the cloud function
   * @param rewardType - The type of reward being claimed
   */
  claimChallengesReward(rewardType: string): Observable<Data> {
    if (!this.userId || !rewardType) {
      return EMPTY;
    }

    const payload = {
      reward_type: rewardType
    };

    return this.authService.getV2CloudFunction(
      'claimchallengesreward',
      payload
    );
  }

}
